import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, sendEmailVerification, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { app } from "./firebase-config.js";
import { PointService } from "./services/point.service.js";
import { locale } from "./services/LocaleService.js";

let firebaseAuth;
let authReadyResolve;
export const waitForAuthReady = new Promise((resolve) => {
    authReadyResolve = resolve;
});

export const signUp = async (email, password) => {
    if (!firebaseAuth) { await window.Modal.alert("인증 시스템이 초기화되지 않았습니다."); return; }
    try {
        const userCredential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
        const user = userCredential.user;
        // await sendEmailVerification(user); // Phase 3: Delegated to Backend Trigger

        // Use New Space Theme Modal
        await window.Modal.showVerificationSuccess(email);

        // [ANALYTICS] Internal Tracking (Signup) - REMOVED (Server Side Only)

        return user;
    } catch (error) {
        console.error("Signup Error:", error);
        throw error;
    }
};

export const login = async (email, password) => {
    if (!firebaseAuth) { await window.Modal.alert("인증 시스템이 초기화되지 않았습니다."); return; }
    try {
        const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
        const user = userCredential.user;

        // Super Admin Bypass
        const isSuperAdmin = (user.email === 'hegler.kim@gmail.com');
        const isTestAccount = (user.email === 'test@ssulwha.world'); // [Toss Reviewer Exception]

        if (!user.emailVerified && !isSuperAdmin && !isTestAccount) {
            // Use New Space Theme Modal
            await window.Modal.showVerificationSuccess(user.email);

            await signOut(firebaseAuth);
            return null;
        }

        // [ANALYTICS] Internal Tracking (Login) - REMOVED (Server Side Only)

        return user;
    } catch (error) {
        console.error("Login Error:", error);
        throw error;
    }
};

export const loginWithGoogle = async () => {
    if (!firebaseAuth) { await window.Modal.alert("인증 시스템이 초기화되지 않았습니다."); return; }
    try {
        const provider = new GoogleAuthProvider();

        // Force account selection every time
        provider.setCustomParameters({
            prompt: 'select_account'
        });

        const result = await signInWithPopup(firebaseAuth, provider);
        const user = result.user;
        return user;
    } catch (error) {
        // [FIX] Suppress "Error" log for user cancellation
        if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
            console.warn("Google Login Cancelled (User Action)");
            throw error;
        }
        console.error("Google Login Error:", error);
        throw error;
    }
};

export const logout = async () => {
    if (!firebaseAuth) return;
    try {
        await signOut(firebaseAuth);
        // Hard Reload to clear all memory/cache
        window.location.reload(true);
    } catch (error) {
        console.error("Logout Error:", error);
        window.location.reload(true); // Failsafe
    }
};

export const resendVerificationEmail = async () => {
    if (!firebaseAuth || !firebaseAuth.currentUser) {
        await window.Modal.alert("로그인이 필요합니다.", "오류");
        return;
    }
    try {
        await sendEmailVerification(firebaseAuth.currentUser);
        // Use New Space Theme Modal
        await window.Modal.showVerificationSuccess(firebaseAuth.currentUser.email);
    } catch (error) {
        console.error("Resend Verification Error:", error);
        await window.Modal.alert("재발송 실패: " + error.message, "오류");
    }
};

window.auth = {
    signUp,
    login,
    loginWithGoogle,
    logout,
    resendVerificationEmail,

    init: async () => {
        try {
            console.log("[Auth] Init Starting... (Logged via auth.js)");

            try {
                firebaseAuth = getAuth(app);
                console.log("[Auth] firebaseAuth instance created.");
            } catch (authError) {
                console.error("[Auth] getAuth failed:", authError);
                await window.Modal.alert("Firebase Auth 초기화 실패: " + authError.message, "시스템 오류");
                return;
            }

            if (!firebaseAuth) {
                console.error("[Auth] Auth instance is null or undefined!");
                await window.Modal.alert("Auth Error: Instance is null", "시스템 오류");
                return;
            }

            // Static Import for Reliability
            const { getFirestore, doc, getDoc, setDoc, updateDoc, serverTimestamp, increment } = await import("firebase/firestore");
            const db = getFirestore(app);
            console.log("[Auth] Firestore connected.");

            onAuthStateChanged(firebaseAuth, async (user) => {
                console.log("[Auth] onAuthStateChanged triggered. User:", user ? user.email : "null");

                if (user) {
                    // [Toss Reviewer Exception] Allow test account
                    if (user.emailVerified || user.email === 'test@ssulwha.world') {
                        try {
                            const userRef = doc(db, "users", user.uid);
                            const userDoc = await getDoc(userRef);

                            user.isSuperAdmin = (user.email === 'hegler.kim@gmail.com');

                            if (userDoc.exists()) {
                                const data = userDoc.data();
                                user.role = data.role || 'user';
                                user.bio = data.bio || ''; // [FIX] Sync Bio field from DB
                                user.authorStatus = data.authorStatus || 'none';
                                user.status = data.status || 'active'; // Default active
                                user.suspensionEndDate = data.suspensionEndDate; // Timestamp

                                // --- GATEKEEPER START ---
                                // Check Ban Status
                                if (user.status === 'banned') {
                                    console.warn("🚫 User is BANNED. Redirecting to Blackhole.");
                                    window.user = user; // Set temp for BannedPage to read
                                    window.router.navigate('/banned');
                                    return;
                                }

                                // Check Suspension Status
                                if (user.status === 'suspended') {
                                    const now = new Date();
                                    const endDate = user.suspensionEndDate ? (user.suspensionEndDate.toDate ? user.suspensionEndDate.toDate() : new Date(user.suspensionEndDate)) : null;

                                    if (endDate && now < endDate) {
                                        console.warn("🚫 User is SUSPENDED until " + endDate);
                                        window.user = user;
                                        window.router.navigate('/banned');
                                        return;
                                    } else {
                                        // Suspension over, auto-recover?
                                        // Ideally, call a Cloud Function to release, or just treat as active.
                                        // For safety, let's update local state to active so they can pass next time?
                                        // Or just allow access.
                                        console.log("✅ Suspension expired. Access granted.");
                                        // Optional: Update DB to 'active' to clean up?
                                        // await updateDoc(userRef, { status: 'active', suspensionEndDate: null });
                                    }
                                }
                                // --- GATEKEEPER END ---

                                user.authorRequestData = data.authorRequestData || {};
                                user.readHistory = data.readHistory || {};

                                if (user.isSuperAdmin && user.role !== 'super_admin') {
                                    await updateDoc(userRef, { role: 'super_admin' });
                                    user.role = 'super_admin';
                                }
                                // [TRACKING] Daily Attendance Check
                                const today = new Date().toISOString().split('T')[0];
                                const lastDate = data.lastAttendanceDate || '';

                                if (lastDate !== today) {
                                    // New Day!
                                    console.log("[Attendance] New day detected. Awarding points.");
                                    // [DYNAMIC] Read attendance amount from admin settings
                                    let attendanceAmount = 10; // Fallback default
                                    let attendanceEnabled = true;
                                    try {
                                        const rwSnap = await getDoc(doc(db, "settings", "activity_rewards"));
                                        if (rwSnap.exists()) {
                                            const rwData = rwSnap.data();
                                            attendanceAmount = rwData.attendance?.amount ?? 10;
                                            attendanceEnabled = rwData.attendance?.enabled ?? true;
                                        }
                                    } catch (e) {
                                        console.warn("[Attendance] Failed to read dynamic config, using default:", e);
                                    }

                                    if (attendanceEnabled) {
                                        try {
                                            await PointService.earnPoints(user.uid, attendanceAmount, 'attendance', '매일 출석 보상');
                                        } catch (e) {
                                            console.error("Daily Login Point Error", e);
                                        }
                                    }

                                    await updateDoc(userRef, {
                                        lastAttendanceDate: today,
                                        visitCount: increment(1),
                                        lastLogin: serverTimestamp()
                                    });

                                    // Alert user (dynamic amount)
                                    if (attendanceEnabled) {
                                        setTimeout(() => {
                                            setTimeout(() => {
                                                const msg = `📅 출석 체크 완료! (Daily Rewards)\n오늘의 보너스 +${attendanceAmount} 포인트 지급!`;
                                                window.Modal.alert(msg, locale.t('auth.attendance_title'));
                                            }, 1000);
                                        }, 1000);
                                    }
                                }

                                // [SYNC] Moved to after try-catch (fires for both new + existing users)

                            } else {
                                // [NEW USER]
                                // 3. Create User Document with PointService Initialization
                                const referralCode = PointService.generateReferralCode();

                                await setDoc(userRef, {
                                    uid: user.uid,
                                    email: user.email,
                                    displayName: user.displayName || 'User',
                                    photoURL: user.photoURL || null,
                                    role: 'user',
                                    authorStatus: 'none',
                                    terms_agreed: false,

                                    // Point System v2
                                    activityPoints: 0,
                                    starPoints: 0,
                                    referralCode: referralCode,

                                    // Legacy Sync (Optional)
                                    points: 0,

                                    visitCount: 1,
                                    lastAttendanceDate: new Date().toISOString().split('T')[0],
                                    createdAt: serverTimestamp(),
                                    lastLogin: serverTimestamp(),
                                    settings: {
                                        emailNotifications: true,
                                        marketingEmails: false
                                    }
                                });

                                // 4. Initial Rewards (Async)
                                // Welcome Bonus
                                await PointService.earnPoints(user.uid, 100, 'welcome', '신규 가입 환영 보너스');

                                // Referral Processing
                                let referralBonus = 0;
                                const storedRefData = sessionStorage.getItem('referralData');
                                if (storedRefData) {
                                    try {
                                        const refData = JSON.parse(storedRefData);
                                        // If we have a specific referral code (e.g. from ?ref=CODE)
                                        // Currently router.js saves utm_source etc.
                                        // We need to check if 'ref' param was captured as source or a dedicated field.
                                        // Assuming router.js might need tweak or we check 'ref' in url param here if session missing?
                                        // Actually, let's just check if we have a logical 'code' to use.
                                        // For now, let's assume the user entered via a link like ?ref=CODE.
                                        // If router.js captured it as 'source'='CODE' (if simple ref) or we need to parse it.

                                        // Let's defer to looking for a 'ref' key in the parsed object if implemented,
                                        // or just check URL params directly here as a fallback?
                                        const search = new URLSearchParams(window.location.search);
                                        const refCode = search.get('ref');

                                        if (refCode) {
                                            await PointService.processReferral(user.uid, refCode);
                                            referralBonus = 500; // Assume success implies 500P bonus
                                        }
                                    } catch (e) {
                                        console.error("Referral Error", e);
                                    }
                                }

                                user.role = user.isSuperAdmin ? 'super_admin' : 'user';
                                user.status = 'active';
                                console.log("✅ Created missing user doc for:", user.email);

                                let msg = locale.t('auth.signup_bonus');
                                if (referralBonus > 0) {
                                    msg = locale.t('auth.referral_bonus');
                                }
                                await window.Modal.alert(msg);
                            }

                        } catch (e) {
                            console.error("🔥 Role fetch/create error", e);
                            user.role = 'user';
                            user.status = 'active'; // Fallback
                        }

                        // [SYNC] Signal Auth Ready (fires for BOTH existing + new users)
                        if (authReadyResolve) authReadyResolve(user);

                        window.user = user;
                        const pendingRedirect = sessionStorage.getItem('auth_redirect');

                        // Determine Redirect
                        // If we are on /banned but status is active (e.g. released), go home.
                        if (window.location.hash === '#/banned' && user.status === 'active') {
                            window.router.navigate('/');
                            return;
                        }

                        // [FIX] Removed Aggressive Redirect on Root/Login
                        // The Router handles navigation. Auth should not force redirect 
                        // just because we are at root, as it traps users trying to 'Home' from a shared link.

                        // if (window.location.hash === '#/login' || window.location.hash === '') { ... } (REMOVED)

                        // Just let the router do its job.
                        // Only execute redirect if explicitly handling a post-login scenario that ISN'T root?
                        // For now, removing this block allows 'Home' to work.

                        // We still consume the token if we are *exactly* at the target, which Router does.
                        // If we are at Login, maybe we DO want to redirect?
                        // But 'Home' triggers this too. 

                        if (window.location.hash === '#/login' && pendingRedirect) {
                            console.log("[Auth] Post-Login Redirect:", pendingRedirect);
                            sessionStorage.removeItem('auth_redirect');
                            window.router.navigate(pendingRedirect);
                        }
                    } else {
                        console.log("User email not verified");
                    }
                } else {
                    console.log("No user logged in (Guest Mode)");
                    window.user = null;
                    if (authReadyResolve) authReadyResolve(null); // [FIX] Resolve Infinite Load for Guests
                }
            });
        } catch (e) {
            console.error("Auth Callback Registration Error:", e);
            await window.Modal.alert("Auth Init Error: " + e.message + "\nCheck Console for details.", "시스템 오류");
            if (window.router) window.router.handleRoute();
            if (authReadyResolve) authReadyResolve(null); // [FIX] Ensure resolution on error
        }
    }
};

window.auth.init();
